import { useState, useCallback } from 'react';
import type { FeatureType, FromStatus, SignatureConfig } from '../types';

/**
 * useSignature フックの戻り値
 */
interface UseSignatureReturn {
  isAgreed: boolean;
  isSigned: boolean;
  canSubmit: boolean;
  setIsAgreed: (value: boolean) => void;
  setIsSigned: (value: boolean) => void;
  clearSignature: () => void;
  getSignatureConfig: (featureType: FeatureType, fromStatus?: FromStatus) => SignatureConfig;
  getCompleteRoute: (featureType: FeatureType, id: string) => string;
}

/**
 * 署名機能を管理するフック
 */
export const useSignature = (): UseSignatureReturn => {
  const [isAgreed, setIsAgreedState] = useState(false);
  const [isSigned, setIsSignedState] = useState(false);

  const setIsAgreed = useCallback((value: boolean) => {
    setIsAgreedState(value);
  }, []);

  const setIsSigned = useCallback((value: boolean) => {
    setIsSignedState(value);
  }, []);

  const clearSignature = useCallback(() => {
    setIsSignedState(false);
  }, []);

  const getSignatureConfig = useCallback((featureType: FeatureType, fromStatus?: FromStatus): SignatureConfig => {
    // 点検の場合（PPTX指示対象外、既存文言維持）
    if (featureType === 'inspection') {
      return {
        title: '点検結果確認',
        description: '枠内で自由に手書きできます。',
        placeholderText: 'ここに署名してください',
        agreementText: '書類の内容を確認しました',
        buttonText: '確認完了',
      };
    }

    // 修繕: "作業報告書確認" から遷移してきた納品サイン
    if (fromStatus === '作業報告書確認') {
      return {
        title: '納品手続き',
        description: '下の枠内にご署名ください',
        placeholderText: 'ここに指で署名してください',
        agreementText: '書類の内容を確認し、作業報告内容に同意します',
        buttonText: '納品手続きを完了する',
      };
    }

    // デフォルト（修繕の注文サイン）
    return {
      title: '注文手続き',
      description: '下の枠内にご署名ください',
      placeholderText: 'ここに指で署名してください',
      consentParagraphs: [
        '本件の修繕対応、住宅・設備および修繕履歴の管理、アフターサービスその他関連サービスの提供・品質管理等のため、HomeDoctor/homekarte（運営会社：FLUCT株式会社）は、サービス連携またはデータ連携を行う提携先事業者に対し、対象物件・設備に関する情報、氏名、連絡先のほか、本件に伴う見積・注文内容、作業内容、支払手続きに関する情報その他本件の対応に必要な情報を、電磁的方法等により提供します。',
        'なお、クレジットカード番号、有効期限およびセキュリティコードは、提供対象に含まれません。',
      ],
      agreementText: '上記の内容を確認し、個人情報等の提供に同意します。',
      buttonText: '注文する',
    };
  }, []);

  const getCompleteRoute = useCallback((featureType: FeatureType, id: string): string => {
    if (featureType === 'inspection') {
      return `/inspections/${id}/sign/complete`;
    }
    return `/inquiries/${id}/sign/complete`;
  }, []);

  return {
    isAgreed,
    isSigned,
    canSubmit: isAgreed && isSigned,
    setIsAgreed,
    setIsSigned,
    clearSignature,
    getSignatureConfig,
    getCompleteRoute,
  };
};
